import type { Accessor } from 'solid-js'
import type { Thread } from '@wovin/core'
import { createEffect, createResource, createSignal } from 'solid-js'
import { Logger } from 'besonders-logger'
import { createPreviewState } from '../ipfs/store-sync'
import { useRawThread } from './reactive'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

/**
 * Hook for managing preview resources with support for incremental updates.
 * Maintains a writable thread internally for efficient delta fetches.
 */
export function usePreviewResource(subID: Accessor<string | null>) {
	const appThread = useRawThread()

	// Store preview state for incremental updates, keyed by pubID
	const previewStateRef: { current: ReturnType<typeof createPreviewState> | null; pubID: string | null } = {
		current: null,
		pubID: null,
	}
	let activeStateId = 0
	let activeLoadId = 0
	const setPreviewState = (state: ReturnType<typeof createPreviewState> | null, pubID: string | null) => {
		previewStateRef.current = state
		previewStateRef.pubID = pubID
		activeStateId += 1
	}

	// Signal to trigger reactivity on incremental updates
	const [updateCounter, setUpdateCounter] = createSignal(0)

	const [previewData, { refetch }] = createResource(
		() => ({ subID: subID(), updateCounter: updateCounter() }),
		async ({ subID: previewOrMissingPub }) => {
			const loadId = ++activeLoadId
			try {
				if (DEBUG.isEnabled) DEBUG(`[usePreviewResource] for`, previewOrMissingPub)
				if (!previewOrMissingPub) {
					setPreviewState(null, null)
					return null
				}

				// Reuse existing state if same pubID (for incremental updates)
				if (previewStateRef.pubID === previewOrMissingPub && previewStateRef.current) {
					DEBUG('[usePreviewResource] Reusing existing preview state')
					return previewStateRef.current.getExtras()
				}

				// Create new preview state and do initial load
				const nextState = createPreviewState(appThread, previewOrMissingPub)
				const isActive = () => loadId === activeLoadId && subID() === previewOrMissingPub
				await nextState.load(undefined, { isActive })
				if (!isActive()) return previewStateRef.current?.getExtras() ?? null
				setPreviewState(nextState, previewOrMissingPub)
				return nextState.getExtras()
			} catch (err) {
				ERROR(`Failed to fetch preview`, previewOrMissingPub, err)
				throw err
			}
		},
	)

	/**
	 * Incremental update - fetches only new applogs since current CID.
	 * Called by realtime watcher when IPNS record updates.
	 */
	const updatePreview = async (newCID?: string) => {
		const currentState = previewStateRef.current
		const currentPubID = previewStateRef.pubID
		if (!currentState) {
			WARN('[usePreviewResource] updatePreview called but no preview state')
			return
		}
		if (currentPubID !== subID()) {
			DEBUG('[usePreviewResource] updatePreview ignored for inactive preview', { currentPubID })
			return
		}
		const stateId = activeStateId
		try {
			const isActive = () => stateId === activeStateId && currentPubID === subID()
			const result = await currentState.update(newCID, { isActive })
			if (!isActive()) return
			if (result.didUpdate) {
				// Trigger reactivity - resource will reuse existing state
				setUpdateCounter(c => c + 1)
			}
		} catch (err) {
			ERROR('[usePreviewResource] Failed to update preview', err)
		}
	}

	DEBUG.isEnabled && createEffect(() => {
		DEBUG(`[usePreviewResource]`, { data: previewData(), state: previewData.state, err: previewData.error })
	})

	return [previewData, { refetch, updatePreview }] as const
}
